// Tags (TAGS.md): an exact port of the parts of tools/tags.py the web app uses (tests: tags_fixtures.json).
import bundled from "../../../specs/taxonomy.json";

export interface Subcategory { id: string; name: string; hint?: string }
export interface Category { id: string; name: string; kind: "topic" | "format" | "source"; subcategories: Subcategory[] }
export interface Taxonomy { version: number; categories: Category[] }
export interface MetaTags {
  auto?: string[]; add?: string[]; remove?: string[]; by?: string; at?: string; version?: number; scores?: Record<string, number>;
}

export const DEFAULT_TAXONOMY = bundled as Taxonomy;
export const TOPIC_MAX = 5;

export function subcategories(t: Taxonomy, kind?: Category["kind"]): [Category, Subcategory][] {
  return t.categories.filter((c) => !kind || c.kind === kind).flatMap((c) => c.subcategories.map((s) => [c, s] as [Category, Subcategory]));
}

export const label = (s: Subcategory) => s.name + (s.hint ? ` (${s.hint})` : "");

const NEWSPAPERS = new Set(["nytimes.com", "washingtonpost.com", "wsj.com", "theguardian.com", "ft.com", "bloomberg.com", "reuters.com",
  "apnews.com", "bbc.com", "bbc.co.uk", "latimes.com", "usatoday.com", "bostonglobe.com", "sfchronicle.com",
  "axios.com", "politico.com", "cnn.com", "nbcnews.com", "cnbc.com", "npr.org", "theinformation.com"]);
const MAGAZINES = new Set(["theatlantic.com", "newyorker.com", "harpers.org", "wired.com", "noemamag.com", "economist.com", "vox.com",
  "nymag.com", "theverge.com", "slate.com", "time.com", "newrepublic.com", "nationalreview.com", "reason.com",
  "technologyreview.com", "quantamagazine.org", "aeon.co", "lrb.co.uk", "nybooks.com", "vanityfair.com",
  "rollingstone.com", "fastcompany.com", "hbr.org", "scientificamerican.com", "worksinprogress.co"]);
const ACADEMIC = new Set(["arxiv.org", "nature.com", "science.org", "ssrn.com", "acm.org", "biorxiv.org", "medrxiv.org", "nber.org",
  "pnas.org", "cell.com", "thelancet.com", "nejm.org", "jstor.org", "openreview.net"]);
const VIDEO = new Set(["youtube.com", "youtu.be", "vimeo.com"]);

function host(url: string | null | undefined): string {
  let h = "";
  try { h = new URL(url || "").hostname.toLowerCase(); } catch { h = ""; }
  return h.startsWith("www.") ? h.slice(4) : h;
}
const inSet = (h: string, d: Set<string>) => [...d].some((x) => h === x || h.endsWith("." + x));

export function sourceTag(url: string | null | undefined, site: string | null | undefined, imageURLs: string[] = []): string {
  const h = host(url), s = (site || "").toLowerCase();
  if (h.endsWith("substack.com") || s.includes("substack") || imageURLs.some((u) => (u || "").includes("substackcdn.com"))) return "substack";
  if (inSet(h, new Set(["x.com", "twitter.com"])) || ["x", "x (formerly twitter)", "twitter"].includes(s)) return "x-twitter";
  if (h.endsWith(".gov") || h.endsWith(".mil") || h.includes(".gov.")) return "government";
  if (h.endsWith(".edu") || h.includes(".ac.") || inSet(h, ACADEMIC)) return "academic";
  if (inSet(h, VIDEO)) return "video";
  if (inSet(h, NEWSPAPERS)) return "newspaper";
  if (inSet(h, MAGAZINES) || ["noema", "harper's magazine", "the atlantic", "the new yorker"].includes(s)) return "magazine";
  return "web";
}

/** Python's json.dumps (default separators, ensure_ascii=False) for a flat object of strings. */
export function pyDumps(obj: Record<string, string>): string {
  return "{" + Object.entries(obj).map(([k, v]) => JSON.stringify(k) + ": " + JSON.stringify(v)).join(", ") + "}";
}

const cp = (s: string, n: number) => { const a = Array.from(s); return a.length <= n ? s : a.slice(0, n).join(""); };

export function articleState(title: string | null, site: string | null, author: string | null, text: string | null) {
  return { title: title || "", site: site || "", author: author || "", text: cp(text || "", 20_000) };
}

export function openrouterMessages(title: string | null, site: string | null, author: string | null, text: string | null, t: Taxonomy) {
  const lines: string[] = [];
  for (const c of t.categories) {
    if (c.kind === "source") continue;
    lines.push(`${c.name}${c.kind === "format" ? " (pick exactly one)" : ""}:`);
    for (const s of c.subcategories) lines.push(`  ${s.id}: ${label(s)}`);
  }
  const system = "You tag articles for a reading app using only the ids in the list below. Pick the topic ids that are "
    + `main subjects of the article (1 to ${TOPIC_MAX}, fewer is better; never a passing mention) and exactly `
    + 'one format id. Reply with JSON only: {"topics": ["id", …], "format": "id"}.\n\n' + lines.join("\n");
  const user = pyDumps(articleState(title, site, author, text ? cp(text, 12_000) : ""));
  return [{ role: "system", content: system }, { role: "user", content: user }];
}

export interface Decision { topics: string[]; format: string | null; scores: Record<string, number> }

export function parseOpenrouter(reply: string | null, t: Taxonomy): Decision | null {
  const m = /\{[\s\S]*\}/.exec(reply || "");
  let data: any = null;
  try { data = m ? JSON.parse(m[0]) : null; } catch { data = null; }
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  const topics = new Set(subcategories(t, "topic").map(([, s]) => s.id));
  const formats = new Set(subcategories(t, "format").map(([, s]) => s.id));
  const chosen: string[] = [];
  for (const x of Array.isArray(data.topics) ? data.topics : []) if (typeof x === "string" && topics.has(x) && !chosen.includes(x)) chosen.push(x);
  const fmt = typeof data.format === "string" && formats.has(data.format) ? data.format : null;
  return { topics: chosen.slice(0, TOPIC_MAX), format: fmt, scores: {} };
}

export function autoTags(d: Decision | null, source: string | null): string[] {
  const tags = d ? [...d.topics] : [];
  if (d?.format) tags.push(d.format);
  if (source) tags.push(source);
  return tags;
}

export function effective(meta: MetaTags | null | undefined, t: Taxonomy): string[] {
  const m = meta || {};
  const remove = new Set(m.remove || []);
  const have = new Set([...(m.auto || []), ...(m.add || [])].filter((x) => !remove.has(x)));
  return subcategories(t).map(([, s]) => s.id).filter((id) => have.has(id));
}

export function edit(meta: MetaTags | null | undefined, id: string, on: boolean): MetaTags {
  const t: MetaTags = { ...(meta || {}) };
  const add = (t.add || []).filter((x) => x !== id), remove = (t.remove || []).filter((x) => x !== id);
  (on ? add : remove).push(id);
  t.add = add.sort(pySort); t.remove = remove.sort(pySort);
  return t;
}

/** Python's sorted() on strings: by code point. */
function pySort(a: string, b: string): number {
  const x = Array.from(a), y = Array.from(b);
  for (let i = 0; i < Math.min(x.length, y.length); i++) {
    const d = x[i].codePointAt(0)! - y[i].codePointAt(0)!;
    if (d) return d;
  }
  return x.length - y.length;
}

export function matches(tags: string[], selected: string[], mode: "all" | "any", t: Taxonomy): boolean {
  if (!selected.length) return true;
  const have = new Set(tags);
  const byCat = new Map(t.categories.map((c) => [c.id, c.subcategories.map((s) => s.id)]));
  const hit = (item: string) => item.startsWith("cat:") ? (byCat.get(item.slice(4)) || []).some((s) => have.has(s)) : have.has(item);
  return mode === "all" ? selected.every(hit) : selected.some(hit);
}

export function counts(all: string[][], t: Taxonomy): Record<string, number> {
  const out: Record<string, number> = {};
  for (const tags of all) {
    const have = new Set(tags);
    for (const c of t.categories) {
      const inCat = c.subcategories.map((s) => s.id).filter((id) => have.has(id));
      for (const id of inCat) out[id] = (out[id] || 0) + 1;
      if (inCat.length) out["cat:" + c.id] = (out["cat:" + c.id] || 0) + 1;
    }
  }
  return out;
}

export function needsTagging(meta: MetaTags | null | undefined, t: Taxonomy): boolean {
  if (!meta || !meta.at) return true;
  return (meta.version || 0) < (t.version ?? 1);
}

/** Names of an article's topic subcategories for its row: tagging's order (auto) first, then the reader's additions. */
export function rowTopics(meta: MetaTags | null | undefined, t: Taxonomy, max = 2): string[] {
  const tags = new Set(effective(meta, t));
  const topics = new Map(subcategories(t, "topic").map(([, s]) => [s.id, s.name]));
  const order = [...(meta?.auto || []), ...(meta?.add || [])].filter((id, i, a) => a.indexOf(id) === i);
  return order.filter((id) => tags.has(id) && topics.has(id)).slice(0, max).map((id) => topics.get(id)!);
}
